import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
	type LWApiPublicMarketPackageDetailsParamsRawI,
	type LWApiPublicMarketPackageDetailsResultRawI,
} from '@liquidweb-dev/club-sauce/public/market/package/index.raw';
import type { DefaultPriceByCycleI } from '@liquidweb-dev/club-sauce/internal/market/package/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import api from 'modules/queries/api';

export interface MarketPackageDetails
	extends LWApiPublicMarketPackageDetailsResultRawI {
	version: LWApiPublicMarketPackageDetailsResultRawI['version'] & {
		bestMonthlyDeal: string | number | undefined;
		mostDiscountedCycle: DefaultPriceByCycleI | undefined;
		formattedBestMonthlyDeal: string;
	};
}

const supplementData = (
	data: LWApiPublicMarketPackageDetailsResultRawI,
): MarketPackageDetails => {
	const { version } = data;
	const { defaultPriceByCycle } = version || {};

	const mostDiscountedCycle =
		defaultPriceByCycle && defaultPriceByCycle.length > 0
			? [...defaultPriceByCycle].sort(
					(a, b) =>
						Number(a.monthly_discount_price) - Number(b.monthly_discount_price),
				)[0]
			: undefined;

	const bestMonthlyDeal = mostDiscountedCycle?.monthly_discount_price;

	const formattedBestMonthlyDeal = formatCurrency(bestMonthlyDeal, {
		timeUnit: 'mo',
	});

	return {
		...data,
		version: {
			...version,
			bestMonthlyDeal,
			mostDiscountedCycle,
			formattedBestMonthlyDeal,
		},
	};
};

export const MARKET_PACKAGE_DETAILS_QUERY_KEY = '/market/package/details';

const usePackageDetails = (
	params: LWApiPublicMarketPackageDetailsParamsRawI,
	options?: UseQueryOptions<
		LWApiPublicMarketPackageDetailsResultRawI,
		ApiErrorI,
		MarketPackageDetails
	>,
) =>
	useQuery({
		queryKey: [MARKET_PACKAGE_DETAILS_QUERY_KEY, params],
		queryFn: () => api.market.package.apiRaw.details(params),
		select: supplementData,
		...options,
	});

export default usePackageDetails;
